// 📎 10-02 room docs (hidden): "Docs for the AIs", a row in the build form's
// Advanced settings right after who lives here (Nina 10-02: "build it out
// now"). It stays HIDDEN until her DMCA agent registration, a Terms line and a
// takedown page exist.
//
// The switch (the buildFormSwitch / SHOW_PORTED_PLACE pattern: an exported
// constant, test-pinned, so turning it on for everyone is a deliberate
// two-file edit, this + pendingRoomDocs.test.js). OFF: nobody sees the row and
// nothing is kept here. Nina previews it on ONE device: a browser holding the
// /admin key opens /custom-space?docs=1 (remembered on that device; ?docs=0
// forgets it).
//
// The files never ride the create call (its body stays byte for byte what it
// was): BuildSpaceV2 keeps them here under the new room's id and hops; the room
// page (components/ChatGame/RoomDocsUpload) takes them once it holds the
// maker's seat (id + secret) and sends each through its own upload with
// knowledge=1. The backend (src/ChatGame/roomDocs.js) keeps the opening of up
// to 3 in the room's AI prompts. The store is a module-level Map: it lives as
// long as this tab's app does (a reload drops it, like any unsent pick).
// Ported rooms (?agentId=) are out on both ends (the backend's roomTakesDocs).
// No React, no network: tested in utils/pendingRoomDocs.test.js.

export const SHOW_ROOM_DOCS = false;
export const ROOM_DOCS_PREVIEW_KEY = 'room_docs_preview';

export function roomDocsPreviewOn(win = (typeof window !== 'undefined' ? window : null)) {
  try {
    const ls = win && win.localStorage;
    if (!ls || !ls.getItem('adminApiKey')) return false;
    const q = new URLSearchParams(win.location.search).get('docs');
    if (q === '1') ls.setItem(ROOM_DOCS_PREVIEW_KEY, '1');
    if (q === '0') ls.removeItem(ROOM_DOCS_PREVIEW_KEY);
    return ls.getItem(ROOM_DOCS_PREVIEW_KEY) === '1';
  } catch (e) { return false; }
}

// The preview runs FIRST, so ?docs=0 still forgets a stored preview once the
// switch is on (buildFormV2On's order).
export const roomDocsOn = (win) => roomDocsPreviewOn(win) || SHOW_ROOM_DOCS;

// ── the files ───────────────────────────────────────────────────────────────
export const ROOM_DOCS_MAX = 3;                 // the backend keeps 3 a room (roomDocs.KNOWLEDGE_MAX)
export const ROOM_DOC_MAX_BYTES = 7 * 1024 * 1024; // chatGameApi.shareFile's own cap
const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const KIND_BY_EXT = Object.freeze({ pdf: 'PDF', docx: 'DOCX', txt: 'TXT', md: 'MD', markdown: 'MD' });
const KIND_BY_MIME = Object.freeze({ 'application/pdf': 'PDF', [DOCX_MIME]: 'DOCX', 'text/plain': 'TXT', 'text/markdown': 'MD', 'text/x-markdown': 'MD' });
// the picker's filter (the same four kinds)
export const ROOM_DOCS_ACCEPT = ['.pdf', '.docx', '.txt', '.md', '.markdown', 'application/pdf', DOCX_MIME, 'text/plain', 'text/markdown'].join(',');

/** 'PDF' | 'DOCX' | 'TXT' | 'MD', or null for anything else. The extension wins over the browser's type. */
export function roomDocKind(file) {
  if (!file || typeof file.name !== 'string') return null;
  const m = /\.([a-z0-9]+)$/i.exec(file.name);
  if (m) return KIND_BY_EXT[m[1].toLowerCase()] || null;
  return KIND_BY_MIME[String(file.type || '').toLowerCase()] || null;
}

const sameFile = (a, b) => a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;

/**
 * The form's list after a pick: `current` + the picked files that fit (a
 * doc kind, not empty, ≤ 7 MB, not already listed, up to ROOM_DOCS_MAX).
 * → { docs, error } with error 'type' | 'empty' | 'big' | 'max' | null (the first miss).
 */
export function addRoomDocs(current, picked) {
  const docs = Array.isArray(current) ? current.slice() : [];
  let error = null;
  for (const f of (picked ? Array.from(picked) : [])) {
    if (!f) continue;
    let miss = null;
    if (!roomDocKind(f)) miss = 'type';
    else if (!(f.size > 0)) miss = 'empty';
    else if (f.size > ROOM_DOC_MAX_BYTES) miss = 'big';
    else if (docs.some((d) => sameFile(d, f))) continue; // already listed
    else if (docs.length >= ROOM_DOCS_MAX) miss = 'max';
    if (miss) { error = error || miss; continue; }
    docs.push(f);
  }
  return { docs, error };
}

// ── the hand-off (form → room page) ─────────────────────────────────────────
const PENDING_TTL_MS = 30 * 60 * 1000; // a room opened much later never surprises anyone with an upload
const pending = new Map();             // game id → { files, at }

/** Keep a new room's docs for its room page (replaces any kept before for that room). */
export function set(gameId, files, now = Date.now()) {
  // 📎 review F8: every keep sweeps what is past the expiry, so a room page that
  // never got its seat does not hold those files for the life of the tab
  for (const [key, hit] of pending) if ((now - hit.at) > PENDING_TTL_MS) pending.delete(key);
  const list = Array.from(files || []).filter(Boolean).slice(0, ROOM_DOCS_MAX);
  if (!gameId || !list.length) return;
  pending.set(String(gameId), { files: list, at: now });
}

/** This room's kept docs, ONCE (taking empties the slot): [] when there are none or they are stale. */
export function take(gameId, now = Date.now()) {
  const key = String(gameId || '');
  const hit = key ? pending.get(key) : null;
  if (!hit) return [];
  pending.delete(key);
  return (now - hit.at) <= PENDING_TTL_MS ? hit.files.slice() : [];
}
